import React from 'react'
import { IMG } from './images'
import { Icon } from './Icon'

export function PageHero({ kicker, title, sub, image = 'facility', crumbs = [], onNavigate }) {
  return (
    <section data-screen-label={title} style={{ position: 'relative', overflow: 'hidden', background: 'var(--grad-hero)', color: '#fff', padding: 'calc(var(--space-9) + 48px) 0 var(--space-9)' }}>
      <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${IMG(image)})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.32 }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(5,15,31,0.9) 0%, rgba(5,15,31,0.65) 55%, rgba(5,15,31,0.35) 100%)' }} />
      <div className="adr-blob" style={{ position: 'absolute', width: 420, height: 420, right: -120, top: -120, background: 'var(--green-500)', borderRadius: '50%', filter: 'blur(110px)', opacity: 0.26 }} />
      <div style={{ position: 'relative', maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 22, fontFamily: 'var(--font-ui)', fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
          <a onClick={() => onNavigate && onNavigate('Home')} style={{ color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}>Home</a>
          {crumbs.map((c) => (
            <React.Fragment key={c}><Icon name="chevron-right" style={{ width: 13, height: 13 }} /><span style={{ color: 'var(--energy-bright)' }}>{c}</span></React.Fragment>
          ))}
        </nav>
        {kicker && <span style={{ display: 'inline-block', fontFamily: 'var(--font-ui)', fontSize: 'var(--fs-eyebrow)', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--energy-bright)', marginBottom: 16 }}>{kicker}</span>}
        <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'var(--fs-display-2)', letterSpacing: '-0.03em', lineHeight: 1.05, margin: '0 0 18px', maxWidth: 880, color: '#fff' }}>{title}</h1>
        {sub && <p style={{ fontSize: 'var(--fs-lead)', color: 'rgba(255,255,255,0.78)', maxWidth: 640, margin: 0 }}>{sub}</p>}
      </div>
    </section>
  );
}

// Full service catalog — drives the Services hub, detail pages, nav mega-menu.
export const SERVICE_CATALOG = {
  bess: {
    icon: 'battery-charging', name: 'Battery Energy Storage Systems', short: 'BESS', image: 'bess', hero: 'bess-page-hero',
    tagline: 'Intelligent, safe and scalable energy storage — from home to grid.',
    intro: 'ADIRATH BESS provides intelligent, safe, and scalable energy storage solutions for residential, commercial and utility applications. Powered by advanced LiFePO₄ technology and a smart BMS, our systems enhance energy efficiency, ensure reliable power availability and maximise renewable energy utilisation.',
    highlights: ['In-House Manufacturing', 'Custom Engineering', 'Scalable Range', 'Quality Certified'],
    // Solution Architecture overview — shown as the architecture diagram section
    architecture: {
      scale: { from: '5 kWh', to: '5,000 kWh' },
      arcModes: [
        {
          icon: 'home', label: 'RESIDENTIAL MODE', tag: 'LV', accent: '#1A80A8',
          img: '/gallery/Bess/240V_3.webp',
          footprint: 'Up to ~50 kWh footprint',
          points: [
            'Suitable for low-energy-footprint LV systems',
            'Plug-and-play installation',
            'Ideal for homes & small offices',
            '5 kW to 15 kW single-module systems',
            'Scalable up to 16 parallel units',
          ],
        },
        {
          icon: 'factory', label: 'C&I HYBRID MODE', tag: 'MV', accent: '#1A9150',
          img: '/gallery/Bess/ADIRATH-BESS_2.webp',
          footprint: '50–600 kWh footprint',
          points: [
            'Plug-and-play with grid + DG support',
            'Rack-mounted systems up to 832V',
            'Modular architecture scalable to 18 series units',
            'Lower energy footprint, easily scalable',
            'Suited for commercial & industrial loads',
          ],
        },
        {
          icon: 'server', label: 'C&I AC-COUPLED MODE', tag: 'HV', accent: '#C47E18',
          img: '/gallery/Bess/1MW BESS.webp',
          footprint: 'Beyond 3.5–5 MWh',
          points: [
            'Suitable for all systems beyond 2.5MW/5 MWh',
            'Can be stepped to suitable voltage levels',
            'Built for utility & heavy industrial sites',
            'Utility-scale solutions up to 1,331.2V & more',
            'Multi-megawatt projects for grid support, large-capacity storage, and renewable integration',
          ],
        },
      ],
    },
    // Individual deep-dive sections per deployment mode
    modes: [
      {
        icon: 'home', label: 'Residential & Small Commercial', tag: 'LV Range',
        sub: 'Plug-and-play energy storage for homes, offices and small commercial sites.',
        img: '/gallery/Bess/240V_1.webp',
        spec: 'Up to 50 kWh  ·  5–15 kW per module',
        variants: 'Variants: 5–20 kWh  |  5–30 kWh  |  5–60 kWh  ·  Scalable up to 16 parallel units',
        features: [
          'Suitable for LV systems with lower energy footprint, typically up to 50 kWh',
          'Modular design allows easy capacity expansion as energy demand grows',
          'Can be coupled with a Diesel Generator (DG) for added resilience',
          'Redundant controls, fault-tolerant design and rapid response ensure high availability',
        ],
        specList: [['Capacity', '5–60 kWh'], ['Power', '5–15 kW'], ['Max Parallel', '16 units'], ['Connectivity', 'LV Grid / DG'], ['Installation', 'Plug & Play']],
      },
      {
        icon: 'factory', label: 'Commercial & Industrial', tag: 'Indoor MV Solution',
        sub: 'Mid-voltage rack-based BESS for factories, campuses and grid-connected commercial facilities.',
        img: '/gallery/Bess/ADIRATH-BESS_2.webp',
        spec: '800 VDC  ·  50–600 kWh  ·  IP21  ·  <10ms changeover',
        variants: 'Rack-mounted up to 832V  ·  Scalable up to 18 series units',
        features: [
          'Mid-voltage 800 VDC system coupled to solar via the same Power Conversion System',
          'Intelligent adaptability to varying load scenarios, suitable for 415V industrial supply',
          'Distributed energy footprint across the facility, IP21 rated for wide temperature range',
          'Off-grid capable with under 10ms changeover — zero disruption to critical loads',
        ],
        specList: [['Capacity', '50–600 kWh'], ['Voltage', '800 VDC'], ['Max Series', '18 units (832V)'], ['IP Rating', 'IP21'], ['Grid Supply', '415V AC'], ['Changeover', '<10ms']],
      },
      {
        icon: 'server', label: 'Utility & Large-Scale C&I', tag: 'Container ESS',
        sub: 'Containerised megawatt-scale storage for utilities, industrial parks and renewable projects.',
        img: '/gallery/Bess/1MW BESS.webp', imgFit: 'contain',
        spec: '1,500V  ·  Up to 5,000 kWh per container  ·  8,000+ cycles',
        variants: 'Standard 20/40 ft container  ·  Multi-container for GWh projects',
        features: [
          'Pre-integrated battery racks, PCS, BMS and fire safety — ready to energise on arrival',
          'Standard 20/40 ft container footprint enables fast transport and installation anywhere',
          'Integrated fire detection & suppression, thermal management and access control',
          'Multiple containers combine seamlessly for grid-scale and large C&I storage projects',
        ],
        specList: [['Capacity', 'Up to 5,000 kWh'], ['System Voltage', '1,500V DC'], ['Cycle Life', '8,000+ cycles'], ['Container', '20 / 40 ft'], ['Scalability', 'Multi-container / GWh']],
      },
    ],
    benefits: [
      ['shield-check', 'Safety-first Design', 'LFP chemistry with multi-layer BMS protection, thermal management and IEC 62619 certification.'],
      ['layers', 'Modular & Scalable', 'Stack from 5 kWh to 5,000 kWh+ without redesign — one architecture, three deployment modes.'],
      ['activity', 'Smart BMS & Monitoring', 'Cell-level telemetry, remote diagnostics and intelligent energy management built in.'],
      ['leaf', 'Clean Energy ROI', 'Peak-shaving, backup power, solar self-consumption and grid support — all in one system.'],
    ],
    specs: [['Capacity', '5 kWh – 5,000 kWh+'], ['Voltage', '48V – 1,500V DC'], ['Chemistry', 'LiFePO₄ (LFP)'], ['Cycle Life', '8,000+ cycles'], ['Changeover', '<10ms (off-grid)'], ['Container', '20 / 40 ft standard']],
  },
  mobility: {
    icon: 'forklift', name: 'Industrial Mobility Batteries', short: 'Mobility', image: 'mobility', hero: 'hero-mobility',
    tagline: 'Powering Industrial Vehicles with Reliable Lithium Battery Technology.',
    intro: 'Purpose-built lithium batteries for industrial vehicles, AGV/AMR automation, EV platforms, marine systems, robotics, and medical mobility — maintenance-free, fast-charging, and engineered for 24/7 uptime.',
    highlights: ['Maintenance-Free', 'Fast Charging', 'Drop-In Replacement', 'Smart BMS Ready'],
    sections: [
      'Industrial Vehicle Batteries — Forklifts, Reach Trucks, Stackers & Aerial Work Platforms',
      'AGV, AMR & ASRS Power Systems — Warehouse & Smart Factory Automation',
      'Electric Vehicle (EV) Battery Solutions — Two-Wheelers to Commercial Platforms',
      'Marine & Special Mobility — Vessels, Patrol Boats & Underwater Systems',
      'Robotics & Automation Batteries — Industrial, Collaborative & Inspection Robots',
      'Medical & Healthcare Mobility — Portable Equipment & Hospital Carts',
    ],
    benefits: [
      ['zap', 'Maintenance-Free Lithium', 'Lightweight, fast-charging lithium solutions replace heavy lead-acid batteries to cut total cost of ownership.'],
      ['repeat', 'High Cycle Life', 'Engineered for continuous multi-shift industrial duty with 3,000+ deep cycles and minimal degradation.'],
      ['gauge', 'Drop-In Replacement', 'Standard and custom form factors for direct lead-acid replacement across all fleet and vehicle types.'],
      ['cpu', 'Smart BMS Integration', 'CAN/RS-485 communication for seamless integration with vehicle controllers and automation systems.'],
    ],
    specs: [
      ['Industrial Vehicles',  '48V – 96V · 30–630 Ah'],
      ['AGV / AMR / ASRS',    '24V – 72V · 24–300 Ah+'],
      ['EV Battery Packs',     '48V – 800V · 80–800 Ah'],
      ['Marine Systems',       '24V – 600V · 40–1000 Ah'],
      ['Chemistries',          'NMC · LTO · LFP'],
      ['Form Factor',          'Drop-in & Custom'],
    ],
    specCategories: [
      {
        icon: 'forklift',
        title: 'Industrial Vehicle Batteries',
        bullets: [
          'Electric Forklifts, Scissor Lifts & Boom Lifts',
          'Reach Trucks, Stackers & Order Pickers',
          'Tow Tractors, Pallet Trucks & Airport Ground Support Equipment',
          'Heavy Industrial & Aerial Work Platform Vehicles',
        ],
        spec: '48V / 60V / 72V / 80V / 96V  |  30Ah to 630Ah  |  NMC · LFP',
      },
      {
        icon: 'bot',
        title: 'AGV | AMR & ASRS Power Systems',
        bullets: [
          'Warehouse Automation & Smart Factories',
          'Automated Logistics, Material Handling & Robotics Platforms',
          'E-commerce Fulfillment Centers & Industry 4.0 Environments',
        ],
        spec: '24V / 36V / 48V / 60V / 72V  |  24Ah to 300Ah+  |  NMC · LTO · LFP',
      },
      {
        icon: 'zap',
        title: 'Electric Vehicle (EV) Battery Solutions',
        bullets: [
          'Electric Scooters, Motorcycles & Three-Wheelers',
          'Electric Utility & Commercial Vehicles',
          'Custom EV Platforms with Intelligent BMS',
        ],
        spec: 'Voltage: 48V to 800V  |  Capacity: 80Ah to 800Ah  |  NMC · LFP',
      },
      {
        icon: 'waves',
        title: 'Marine & Special Mobility Batteries',
        bullets: [
          'Marine Vessels, Patrol Boats & Passenger Ferries',
          'Underwater Equipment, ROVs & Offshore Platforms',
          'Defence Mobility & Specialized Industrial Equipment',
        ],
        spec: 'Voltage: 24V to 600V  |  Capacity: 40Ah to 1000Ah',
      },
    ],
    showcaseModes: true,
    modes: [
      {
        icon: 'forklift',
        tag: 'Industrial Vehicle Batteries',
        label: 'Powering Industrial Vehicles with Reliable Lithium Battery Technology',
        sub: 'EEE Energy Private Limited designs and manufactures high-performance lithium battery systems for industrial vehicles that demand maximum uptime, efficiency, and safety. Our batteries are engineered to replace conventional lead-acid batteries with lightweight, maintenance-free, and fast-charging lithium solutions.',
        variants: 'Whether you operate forklifts, pallet trucks, reach trucks, stackers, tow tractors, or specialized industrial vehicles, our battery systems deliver superior performance while reducing operating costs and increasing productivity.',
        img: IMG('mobility'),
        features: ['Electric Forklifts, Scissor Lifts & Boom Lifts', 'Reach Trucks', 'Stackers', 'Tow Tractors', 'Order Pickers', 'Pallet Trucks', 'Airport Ground Support Equipment', 'Heavy Industrial Vehicles'],
      },
      {
        icon: 'bot',
        tag: 'AGV, AMR & ASRS Power Systems',
        label: 'Intelligent Battery Solutions for Smart Automation',
        sub: 'Automation begins with reliable power. EEE Energy Private Limited develops advanced lithium battery systems specifically designed for Automated Guided Vehicles (AGVs), Autonomous Mobile Robots (AMRs), and Automated Storage & Retrieval Systems (ASRS).',
        variants: 'Our compact, lightweight, and high-performance battery packs provide uninterrupted operation for warehouse automation, logistics, manufacturing, and Industry 4.0 environments.',
        img: IMG('automation'),
        features: ['Warehouse Automation', 'Smart Factories', 'Automated Logistics', 'Material Handling', 'Robotics Platforms', 'E-commerce Fulfillment Centers'],
      },
      {
        icon: 'zap',
        tag: 'Electric Vehicle (EV) Battery Solutions',
        label: 'Advanced Lithium Battery Packs for Electric Mobility',
        sub: 'EEE Energy Private Limited develops customized lithium battery packs for electric vehicles ranging from two-wheelers to commercial electric mobility platforms.',
        variants: 'Our EV batteries combine safety, efficiency, and intelligent battery management to deliver exceptional driving range, performance, and long operational life.',
        img: IMG('bess'),
        features: ['Electric Scooters', 'Electric Motorcycles', 'Electric Three-Wheelers', 'Electric Utility Vehicles', 'Electric Commercial Vehicles', 'Custom EV Platforms'],
      },
      {
        icon: 'waves',
        tag: 'Marine & Special Mobility Batteries',
        label: 'Rugged Lithium Battery Systems for Marine and Extreme Environments',
        sub: 'EEE Energy Private Limited develops highly reliable lithium battery systems engineered for harsh operating conditions including marine applications, offshore equipment, underwater systems, and specialized mobility platforms.',
        variants: 'Designed with corrosion-resistant materials and advanced sealing technology, our batteries ensure dependable performance even in demanding environments.',
        img: IMG('automation2'),
        features: ['Marine Vessels', 'Patrol Boats', 'Underwater Equipment', 'Offshore Platforms', 'Defence Mobility', 'Specialized Industrial Equipment'],
      },
      {
        icon: 'cpu',
        tag: 'Robotics & Automation Batteries',
        label: 'Reliable Power for Next Generation Robotics',
        sub: 'Modern robotics requires compact, lightweight, and intelligent battery systems capable of delivering high peak currents while maintaining long operating hours.',
        variants: 'EEE Energy Private Limited designs custom lithium battery packs for industrial robots, collaborative robots, inspection robots, service robots, and mobile robotic platforms.',
        img: IMG('robotics'),
        features: ['Industrial Robots', 'Collaborative Robots', 'Mobile Robots', 'Inspection Robots', 'Warehouse Robots', 'Service Robotics'],
      },
      {
        icon: 'heart-pulse',
        tag: 'Medical & Healthcare Mobility Batteries',
        label: 'Precision Battery Solutions for Medical Equipment',
        sub: 'EEE Energy Private Limited manufactures highly reliable lithium battery systems for medical and healthcare equipment where uninterrupted power and patient safety are critical.',
        variants: 'Our batteries comply with stringent quality standards while delivering dependable backup and portable power for critical medical applications.',
        img: IMG('facility'),
        features: ['Portable Medical Equipment', 'Patient Monitoring Systems', 'Diagnostic Equipment', 'Medical Mobility Devices', 'Portable Ventilators', 'Hospital Carts'],
      },
    ],
  },
  robotics: {
    icon: 'bot', name: 'Robotics System Integration', short: 'Robotics', image: 'robotics', hero: 'hero-robotics',
    tagline: 'Turnkey robotic cells, engineered around your process.',
    intro: 'End-to-end industrial robot integration services that improve manufacturing productivity, precision, and workplace safety — from single-arm pick-and-place to full automated welding and custom robotic cells.',
    showcaseModes: true,
    modes: [
      {
        icon: 'bot',
        tag: 'INTEGRATION',
        label: 'Industrial Robot Integration',
        sub: 'End-to-end industrial robot integration that ensures seamless communication between robots, PLCs, HMIs, sensors, and production equipment.',
        variants: 'We integrate robotic systems with existing manufacturing lines across automotive, electronics, food processing, pharmaceuticals, and heavy engineering sectors — delivering precision, safety and measurable productivity gains.',
        features: ['Robot Selection', 'Mechanical Integration', 'End-of-Arm Tooling', 'Robot Programming', 'Safety Integration', 'Commissioning'],
        img: '/photos/robotics-3.webp',
      },
      {
        icon: 'hand',
        tag: 'AUTOMATION',
        label: 'Pick & Place Automation',
        sub: 'High-speed automated material handling systems that increase production speed while minimising human intervention across the entire line.',
        variants: 'Our pick-and-place solutions cover robotic handling of components, packaging, sorting, assembly, palletising and depalletising — delivering continuous, flexible automation at scale.',
        features: ['Increased Productivity', 'Reduced Labour Cost', 'High Accuracy', 'Continuous Operation', 'Flexible Automation'],
        img: '/photos/robotics-4.webp',
      },
      {
        icon: 'zap',
        tag: 'WELDING',
        label: 'Robotic Welding Solutions',
        sub: 'Precision automated welding for MIG, TIG, Spot Welding, Laser Welding, and specialised industrial applications — improving consistency and throughput.',
        variants: 'Our systems reduce defects and increase manufacturing throughput for automotive, heavy engineering, sheet metal, structural fabrication, and pressure vessel applications.',
        features: ['Automotive', 'Heavy Engineering', 'Sheet Metal', 'Structural Fabrication', 'Pressure Vessels'],
        img: '/photos/robotics-5.webp',
      },
      {
        icon: 'settings-2',
        tag: 'CUSTOM',
        label: 'Custom Robotics Solutions',
        sub: 'Tailor-made robotic automation systems built around customer-specific production requirements for specialised industries and unique processes.',
        variants: 'Our engineering team develops fully customised solutions — from vision-guided and AI-enabled robotics to inspection, assembly, machine tending and packaging automation.',
        features: ['Inspection Robots', 'Assembly Automation', 'Machine Tending', 'Packaging Automation', 'Vision-Guided Robotics', 'AI-Enabled Robotics'],
        img: '/photos/robotics-6.webp',
      },
    ],
    sections: [
      'Industrial Robot Integration — End-to-end integration of robots with PLCs, HMIs, sensors and production equipment.',
      'Pick & Place Automation — High-speed material handling with minimal human intervention.',
      'Robotic Welding Solutions — Precision MIG, TIG, Spot and Laser welding for consistent quality.',
      'Custom Robotics Solutions — Bespoke robotic systems built around your specific production requirements.',
    ],
    benefits: [
      ['bot', 'Seamless Integration', 'Robots communicate flawlessly with PLCs, HMIs, sensors and existing production equipment.'],
      ['trending-up', 'Higher Throughput', 'Faster cycles and continuous 24/7 operation with minimal downtime.'],
      ['shield-check', 'Built-in Safety', 'CE-compliant guarding, safety PLCs and risk-assessed cell design.'],
      ['settings-2', 'Flexible Automation', 'Scalable systems that adapt to evolving production requirements and product variants.'],
    ],
    specs: [['Payloads', '3 – 500 kg'], ['Brands', 'Multi-vendor'], ['Reach', 'Up to 3.1 m'], ['Safety', 'PLd / SIL2'], ['Welding', 'MIG / TIG / Spot / Laser'], ['Vision', 'AI-enabled guidance']],
  },
  automation: {
    icon: 'cog', name: 'Industrial Automation', short: 'Automation', image: 'automation', hero: 'hero-automation',
    tagline: 'Smart systems that connect, monitor and optimise your entire operation.',
    intro: 'Automation revolutionises industries by streamlining operations, enhancing efficiency, and reducing human intervention in complex processes. At ADIRATH, we deliver advanced automation solutions that integrate seamlessly into your existing systems — from PLC-based robotic pick-and-place to AI-powered smart manufacturing and IoT-enabled smart factories.',
    highlights: ['PLC / HMI Systems', 'IoT Integration', 'AI-Powered', 'Proven Projects'],
    modes: [
      {
        icon: 'settings-2', label: 'Automation', tag: 'Industries',
        img: '/photos/industrialRobotarm.webp',
        sub: 'Automation revolutionises industries by streamlining operations, enhancing efficiency, and reducing human intervention in complex processes. It involves the use of advanced technologies to perform tasks that humans traditionally carried out. From manufacturing to logistics, automation is driving innovation and enabling businesses to achieve unprecedented levels of precision, speed, and productivity.',
        variants: 'At EEE Energy Private Limited, we are at the forefront of innovation in automation, providing advanced solutions that integrate seamlessly into your existing systems to enhance efficiency, reliability, and productivity. Our expertise in robotics and automation, combined with our commitment to cutting-edge technology, positions us as a leader in the industry.',
        features: [],
      },
      {
        icon: 'settings-2', label: 'Customised Automation Solutions', tag: 'SOLUTIONS', img: '/photos/pr-train.webp',
        sub: 'We specialise in delivering bespoke automation solutions tailored to meet the unique needs of our clients. Our offerings include:',
        cards: [
          { icon: 'bot', title: 'Robotic Automation', desc: 'Advanced robotic systems designed to automate complex tasks, improve precision, and reduce operational costs.' },
          { icon: 'git-merge', title: 'Process Automation', desc: 'Streamlined processes using state-of-the-art automation technologies to enhance productivity and minimise errors.' },
          { icon: 'factory', title: 'Industrial Automation', desc: 'Integration of automation systems into industrial settings to optimise operations and ensure seamless workflows.' },
        ],
        features: [],
      },
      {
        icon: 'cpu', label: 'Smart Manufacturing', tag: 'SMART MFG', img: '/photos/pr-support.webp',
        sub: 'Our smart manufacturing solutions leverage technological advancements to transform your manufacturing processes. Key features include:',
        cards: [
          { icon: 'wifi', title: 'IoT Integration', desc: 'Harness the power of the Internet of Things (IoT) to connect devices, collect data, and monitor operations in real time.' },
          { icon: 'brain', title: 'AI-Powered Systems', desc: 'Implement artificial intelligence to predict maintenance needs, optimise production schedules, and improve quality control.' },
          { icon: 'sliders-horizontal', title: 'Advanced Control Systems', desc: 'Utilise sophisticated control systems to better manage and oversee your manufacturing operations.' },
        ],
        features: [],
      },
      {
        icon: 'rocket', label: 'Future-Ready Automation', tag: 'FUTURE',
        img: '/photos/PLC_Automation.webp',
        sub: 'We are continuously exploring new horizons in automation, with a focus on innovation and excellence. Our future-ready automation solutions include:',
        cards: [
          { icon: 'battery-charging', title: 'Modular Battery Packs with AI Integration', desc: 'Advanced modular battery packs integrated with artificial intelligence for smarter energy storage and management.' },
          { icon: 'shield', title: 'Defence Solutions', desc: 'Cutting-edge automation technologies tailored for defence applications, ensuring superior performance and reliability in critical scenarios.' },
          { icon: 'network', title: 'IoT and Smart Solutions', desc: 'Developing IoT-enabled smart solutions to revolutionise energy management and automation.' },
        ],
        features: [],
      },
    ],
    benefits: [
      ['settings-2', 'Custom Engineering', 'Every solution designed and built around your specific process, product and production targets.'],
      ['wifi', 'IoT-Connected', 'Real-time device connectivity and data collection for full operational visibility.'],
      ['cpu', 'AI-Powered Intelligence', 'Predictive maintenance, adaptive scheduling and AI-driven quality control across the line.'],
      ['shield-check', 'Proven Project Delivery', 'Executed automation projects for leading manufacturers — on scope, on time, in full.'],
    ],
    specs: [['Controllers', 'PLC / HMI / SCADA'], ['Protocols', 'OPC-UA, MQTT, Profinet'], ['Applications', 'Pick & Place, Punching, Assembly'], ['Safety', 'IEC 62061 / PLd'], ['Vision', '2D & 3D Machine Vision'], ['AI', 'Predictive Maintenance']],
    projectsExecuted: {
      sub: 'Smart Manufacturing  •  Industry 4.0  •  Digital Transformation',
      items: [
        { icon: 'bot',           title: 'Machine Tending Automation',      tags: ['Automated Loading', 'Automated Unloading', 'Increased Productivity'] },
        { icon: 'hand',          title: 'Pick & Place Automation',          tags: ['High Speed', 'Vision Guided', 'Accurate Handling'] },
        { icon: 'package-check', title: 'End-of-Line Automation',           tags: ['Packaging', 'Inspection', 'Palletising & Logistics'] },
        { icon: 'eye',           title: 'Vision System Integration',        tags: ['AI Inspection', 'Defect Detection', 'Quality Assurance'] },
        { icon: 'monitor',       title: 'PLC, HMI & Control Integration',   tags: ['PLC Systems', 'HMI Interface', 'SCADA Integration'] },
        { icon: 'network',       title: 'Smart Factory & Industry 4.0',     tags: ['IoT Enable', 'Cloud Connected', 'Real-Time Analytics'] },
      ],
    },
  },
  solar: {
    icon: 'sun', name: 'Solar EPC Solutions', short: 'Solar EPC', image: 'solar', hero: 'hero-solar',
    tagline: 'Committed to Harnessing the Eternal Source of Solar Energy.',
    intro: 'One of India\'s leading Solar EPC companies — delivering turnkey solar solutions for residential, commercial, and industrial applications under both CAPEX and OPEX models.',
    showcaseModes: true,
    modes: [
      {
        icon: 'sun',
        tag: 'SOLAR ENERGY',
        label: 'Empowering India with Solar Energy',
        sub: 'EEE Energy Private Limited is one of India\'s leading Solar EPC companies dedicated to accelerating India\'s transition to renewable power. We specialize in designing, engineering, and delivering turnkey solar solutions for residential, commercial, and industrial applications.',
        variants: 'With over 6 years of expertise in the renewable energy sector, we offer fully integrated EPC services under both CAPEX and OPEX models, helping customers achieve energy independence and long-term savings.',
        features: ['Solar Energy Solutions', 'Green and Sustainable Products', 'Solar + EV + Energy Storage (Lithium Batteries)', 'Smart and AI-enabled Energy Systems'],
        img: '/photos/solar.webp',
      },
      {
        icon: 'sun',
        tag: 'EPC',
        label: 'Solar EPC Solutions',
        sub: 'We offer complete Engineering, Procurement, and Construction (EPC) services for solar power projects, ensuring seamless execution from concept to commissioning. Our solar projects range from small rooftop installations to Megawatt-scale industrial systems, Customized to meet diverse power demands.',
        focusAreas: ['Solar Energy Solutions', 'Green and Sustainable Products', 'Technological Initiatives: **Solar + EV + Energy Storage (Lithium Batteries)**', 'Smart and AI-enabled Energy Systems'],
        vision: 'To become a globally recognized leader in **sustainable energy solutions**, driving innovation through **solar power, advanced energy storage, and automation technologies**, and supporting India\'s clean energy mission.',
        img: '/photos/solar-panels-roof-solar-cell.webp',
        subCardsTitle: 'Advanced Robotics Solutions',
        subCards: [
          { icon: 'hard-hat', title: 'Engineering', items: ['Site survey, feasibility study, and shading analysis', 'Detailed electrical and structural design', 'System simulation and performance modelling.'] },
          { icon: 'package', title: 'Procurement', items: ['Supply of Tier-1 certified solar modules and inverters', 'Vendor evaluation and quality inspection', 'Compliance with IEC, MNRE, and BIS standards'] },
          { icon: 'building-2', title: 'Construction', items: ['Civil and mechanical installation', 'Electrical wiring, testing, and commissioning', 'Grid interconnection and safety compliance'] },
          { icon: 'hand-coins', title: 'Financial Overview', items: ['Transparent cost breakdown (equipment, installation, labor, logistics)', 'Flexible payment milestones', 'ROI and payback period estimation', 'Support for net metering and government subsidy schemes'] },
        ],
      },
      {
        icon: 'activity',
        tag: 'O&M',
        label: 'Operation & Maintenance (O&M) Services',
        sub: 'Post-installation, we provide comprehensive O&M services to ensure optimal performance and maximum energy yield.',
        featuresLabel: 'Our O&M Offerings',
        features: ['Scheduled preventive maintenance', 'IoT-based remote performance monitoring', 'Solar panel cleaning and system health checks', 'Annual Maintenance Contracts (AMC)', 'Real-time analytics and reporting dashboards'],
        img: '/photos/Solaranlage, Photovoltaik, Solar Panele.webp',
      },
      {
        icon: 'battery-charging',
        tag: 'STORAGE',
        label: 'Integrated Solar + Storage Solutions',
        sub: 'We integrate Solar PV systems with advanced Lithium Battery Storage for uninterrupted energy and enhanced efficiency.',
        featuresLabel: 'Key Features',
        features: ['Backup during grid outages', 'Load shifting and peak shaving', 'Modular energy storage using Lithium-Ion or LiFePO₄ batteries', 'Integration with EV charging and hybrid inverters'],
        img: '/photos/service-bess.webp',
      },
    ],
    sections: ['Rooftop Solar', 'Ground-Mounted Solar', 'Solar + BESS', 'EPC Services', 'Engineering & Design', 'Industrial Solar'],
    benefits: [['sun', 'Maximised yield', 'Optimised layout, string design and tracking.'], ['battery-charging', 'Solar + storage', 'Round-the-clock clean power.'], ['file-check', 'Full EPC', 'Single accountable partner end-to-end.'], ['hand-coins', 'Strong ROI', 'Fast payback and predictable savings.']],
    specs: [['Scale', '10 kW – 50 MW+'], ['Types', 'Rooftop / ground / hybrid'], ['Services', 'Design → O&M'], ['Standards', 'IEC / MNRE']],
  },
  npd: {
    icon: 'rocket', name: 'New Product Development', short: 'NPD', image: 'npd', hero: 'hero-npd',
    tagline: 'Research-driven engineering for mission-critical sectors.',
    intro: 'A dedicated R&D practice developing batteries, charging and control systems for defence, aerospace, marine and railways — from concept to certified product.',
    benefits: [['flask-conical', 'In-house R&D', 'Cell, electronics and firmware under one roof.'], ['ruler', 'Built to spec', 'Ruggedised to MIL / rail / marine standards.'], ['git-branch', 'Full lifecycle', 'Concept → prototype → certification → production.'], ['lock', 'IP & compliance', 'Secure, traceable development.']],
    specs: [['Sectors', 'Defence · Aero · Marine · Rail'], ['Stages', 'Concept → production'], ['Standards', 'MIL / DO / EN'], ['Focus', 'Battery · BMS · Charging']],
    hideSpecCard: true,
    introSection: {
      title: 'New Product Research and Development',
      body: 'At EEE Energy Private Limited, we are passionate about innovation and committed to driving technological advancements through our dedicated Research and Development (R&D) initiatives. Our R&D efforts focus on creating cutting-edge solutions that meet the evolving needs of our customers and the industry at large.',
      img: '/photos/pr-proto.webp',
    },
    approachSection: {
      title: 'Our Approach to R&D',
      sub: 'Our approach to research and development is centred on a few key principles:',
      items: [
        { icon: 'lightbulb', title: 'Innovation', desc: 'We continuously explore new ideas and technologies to develop products that are at the forefront of industry advancements.' },
        { icon: 'users', title: 'Collaboration', desc: 'We collaborate with industry experts, academic institutions, and technology partners to leverage diverse expertise and accelerate innovation.' },
        { icon: 'leaf', title: 'Sustainability', desc: 'Our R&D efforts prioritize sustainable practices and the development of eco-friendly products that contribute to a greener future.' },
        { icon: 'heart-handshake', title: 'Customer-Centric', desc: 'We place our customers at the heart of our R&D activities, ensuring that our innovations address their specific needs and challenges.' },
      ],
    },
    focusAreasSection: {
      title: 'Focus Areas',
      sub: 'Our R&D team is dedicated to exploring and developing new products in several key areas:',
      items: [
        { icon: 'brain', title: 'Artificial Intelligence in Modular Battery Packs', desc: 'We are pioneering the integration of AI into modular battery packs to enhance performance, predict maintenance needs, and optimize energy usage.' },
        { icon: 'wifi', title: 'IoT Solutions', desc: 'Our R&D efforts are focused on creating advanced IoT solutions that enable smarter energy management, real-time monitoring, and seamless connectivity across devices and systems.' },
        { icon: 'shield', title: 'Defence Solutions', desc: 'We are committed to developing robust and reliable defence solutions that leverage the latest technologies to ensure superior performance in critical scenarios.' },
        { icon: 'sun', title: 'Renewable Energy Technologies', desc: 'Our research is aimed at advancing renewable energy technologies, including solar, wind, and energy storage solutions, to support a sustainable energy future.' },
        { icon: 'cpu', title: 'Advanced Battery Management Systems', desc: 'We are developing next-generation battery management systems that offer improved efficiency, safety, and lifespan for various applications.' },
      ],
    },
    innovationsSection: {
      title: 'Innovations in Action',
      sub: 'Our commitment to R&D has led to several ground-breaking innovations:',
      items: [
        { icon: 'cpu', title: 'Smart Battery Management', desc: 'Development of AI-powered battery management systems that provide real-time data analytics, predictive maintenance, and optimal energy distribution.' },
        { icon: 'network', title: 'IoT-Enabled Smart Grids', desc: 'Creation of IoT-enabled smart grid solutions that enhance the efficiency, reliability, and resilience of energy distribution networks.' },
        { icon: 'bot', title: 'Robotic Automation in Energy', desc: 'Integration of advanced robotics in energy management, including automated monitoring and maintenance of renewable energy installations.' },
      ],
    },
    facilitiesSection: {
      title: 'STATE-OF-THE-ART R&D Facilities',
      sub: 'Our state-of-the-art R&D facilities are equipped with the latest tools and technologies, providing an ideal environment for innovation and experimentation. Our facilities include:',
      items: [
        { icon: 'flask-conical', title: 'Advanced Laboratories', desc: 'Cutting-edge labs equipped with the latest equipment for testing and development.' },
        { icon: 'box', title: 'Prototyping and Testing', desc: 'Facilities for rapid prototyping and rigorous testing to ensure our products meet the highest standards of quality and performance.' },
        { icon: 'users', title: 'Collaboration Spaces', desc: 'Dedicated spaces for collaboration with partners, researchers, and clients to foster innovation and knowledge sharing.' },
      ],
    },
  },
};

export const SERVICE_ORDER = ['bess', 'mobility', 'robotics', 'automation', 'solar', 'npd'];
